/** The smallest DOM helper that keeps the UI files readable. No framework. */

import { sfx } from '../audio/sfx'

type Props = Record<string, string | number | boolean | EventListener | undefined>

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  children: Array<Node | string | null | undefined> = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || v === false) continue
    if (k.startsWith('on') && typeof v === 'function') {
      node.addEventListener(k.slice(2).toLowerCase(), v as EventListener)
    } else if (k === 'class') {
      node.className = String(v)
    } else if (k === 'text') {
      node.textContent = String(v)
    } else if (k === 'html') {
      node.innerHTML = String(v)
    } else {
      node.setAttribute(k, String(v))
    }
  }
  for (const c of children) {
    if (c === null || c === undefined) continue
    node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c)
  }
  return node
}

/**
 * Every button in the game goes through here, which is why the click sound lives here too: one place
 * to hook means no screen can be silently missed. It doubles as the AudioContext unlock — the first
 * button a player ever presses is what earns us permission to make noise later.
 */
export function button(label: string, onClick: () => void, cls = 'btn'): HTMLButtonElement {
  return el('button', {
    class: cls,
    type: 'button',
    text: label,
    onclick: () => {
      sfx.resume()
      sfx.play('click')
      onClick()
    },
  })
}

export function clear(node: HTMLElement): void {
  node.textContent = ''
}

/** Short-lived message at the top of the screen. */
export function toast(host: HTMLElement, message: string, ms = 2200): void {
  const t = el('div', { class: 'toast', text: message })
  host.appendChild(t)
  setTimeout(() => {
    t.classList.add('toast-out')
    setTimeout(() => t.remove(), 320)
  }, ms)
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

export function formatSeconds(s: number): string {
  const clamped = Math.max(0, s)
  return clamped < 10 ? clamped.toFixed(1) : Math.ceil(clamped).toString()
}
